import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../config/api';

interface Address {
  line1: string; line2: string; city: string; state: string; postalCode: string; country: string;
}

interface Contact {
  name: string; title: string; email: string; phone: string;
}

interface BankInfo {
  bankName: string; accountNumber: string; accountHolderName: string;
  routingNumber: string; swiftCode: string; iban: string; bankAddress: string;
}

interface ShipperDetails {
  shipperName: string; shipperAccountNumber: string; shipperContact: string; freightTerms: string;
}

interface LookupResult {
  registrationNumber: string;
  status: string;
  entityName: string;
  approvalNotes?: string;
  requestedSections?: string[];
  email?: string;
  phone?: string;
  mobile?: string;
  taxId?: string;
  registrationType?: string;
  primaryContact?: Contact;
  secondaryContact?: Contact;
  businessAddress?: Address;
  remittanceAddress?: Address;
  bankInfo?: BankInfo;
  shipperDetails?: ShipperDetails;
  paymentTerms?: string;
  currency?: string;
  businessType?: string;
  notes?: string;
  specialRequirements?: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending Review', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' },
  approved: { label: 'Approved', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  rejected: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  changes_requested: { label: 'Changes Requested', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
};

const SECTION_LABELS: Record<string, string> = {
  contact_info: 'Phone / Contact Information',
  primary_contact: 'Primary Contact',
  secondary_contact: 'Secondary Contact',
  business_address: 'Business Address',
  remittance_address: 'Remittance Address',
  financial_info: 'Financial Information',
  bank_details: 'Bank Details',
  business_details: 'Business Details',
  shipper_details: 'Shipper Details',
  notes: 'Notes / Special Requirements',
};

const RegistrationLookupPage: React.FC = () => {
  const [regNumber, setRegNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Editable fields
  const [editPhone, setEditPhone] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSpecialReqs, setEditSpecialReqs] = useState('');
  const [editPrimaryContact, setEditPrimaryContact] = useState<Contact>({ name: '', title: '', email: '', phone: '' });
  const [editSecondaryContact, setEditSecondaryContact] = useState<Contact>({ name: '', title: '', email: '', phone: '' });
  const [editBusinessAddress, setEditBusinessAddress] = useState<Address>({ line1: '', line2: '', city: '', state: '', postalCode: '', country: '' });
  const [editRemittanceAddress, setEditRemittanceAddress] = useState<Address>({ line1: '', line2: '', city: '', state: '', postalCode: '', country: '' });
  const [editBankInfo, setEditBankInfo] = useState<BankInfo>({ bankName: '', accountNumber: '', accountHolderName: '', routingNumber: '', swiftCode: '', iban: '', bankAddress: '' });
  const [editShipperDetails, setEditShipperDetails] = useState<ShipperDetails>({ shipperName: '', shipperAccountNumber: '', shipperContact: '', freightTerms: '' });
  const [editPaymentTerms, setEditPaymentTerms] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [editBusinessType, setEditBusinessType] = useState('');

  const sections = result?.requestedSections || [];
  const hasSection = (key: string) => sections.length === 0 || sections.includes(key);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setIsEditing(false);
    setSubmitSuccess(false);

    if (!regNumber.trim() || !email.trim()) {
      setError('Please enter both registration number and email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get('/registrations/lookup', {
        params: { registrationNumber: regNumber.trim(), email: email.trim() },
      });
      const data = response.data.data;
      setResult(data);

      if (data.status === 'changes_requested' || data.status === 'rejected') {
        setIsEditing(true);
        setEditPhone(data.phone || '');
        setEditMobile(data.mobile || '');
        setEditNotes(data.notes || '');
        setEditSpecialReqs(data.specialRequirements || '');
        setEditPrimaryContact(data.primaryContact || { name: '', title: '', email: '', phone: '' });
        setEditSecondaryContact(data.secondaryContact || { name: '', title: '', email: '', phone: '' });
        setEditBusinessAddress(data.businessAddress || { line1: '', line2: '', city: '', state: '', postalCode: '', country: '' });
        setEditRemittanceAddress(data.remittanceAddress || { line1: '', line2: '', city: '', state: '', postalCode: '', country: '' });
        setEditBankInfo(data.bankInfo || { bankName: '', accountNumber: '', accountHolderName: '', routingNumber: '', swiftCode: '', iban: '', bankAddress: '' });
        setEditShipperDetails(data.shipperDetails || { shipperName: '', shipperAccountNumber: '', shipperContact: '', freightTerms: '' });
        setEditPaymentTerms(data.paymentTerms || '');
        setEditCurrency(data.currency || '');
        setEditBusinessType(data.businessType || '');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to look up registration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const updates: any = {};

      if (hasSection('contact_info')) {
        updates.phone = editPhone;
        updates.mobile = editMobile || undefined;
      }
      if (hasSection('primary_contact')) {
        updates.primaryContact = editPrimaryContact;
      }
      if (hasSection('secondary_contact')) {
        updates.secondaryContact = editSecondaryContact.name ? editSecondaryContact : undefined;
      }
      if (hasSection('business_address')) {
        updates.businessAddress = editBusinessAddress;
      }
      if (hasSection('remittance_address')) {
        updates.remittanceAddress = editRemittanceAddress;
      }
      if (hasSection('financial_info')) {
        updates.paymentTerms = editPaymentTerms;
        updates.currency = editCurrency;
      }
      if (hasSection('bank_details')) {
        updates.bankInfo = editBankInfo;
      }
      if (hasSection('business_details')) {
        updates.businessType = editBusinessType;
      }
      if (hasSection('shipper_details')) {
        updates.shipperDetails = editShipperDetails;
      }
      if (hasSection('notes')) {
        updates.notes = editNotes || undefined;
        updates.specialRequirements = editSpecialReqs || undefined;
      }

      await api.put('/registrations/lookup', {
        registrationNumber: regNumber.trim(),
        email: email.trim(),
        updates,
      });
      setSubmitSuccess(true);
      setIsEditing(false);
      setResult(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to resubmit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusInfo = result ? STATUS_LABELS[result.status] || STATUS_LABELS.pending : null;

  // --- Render helpers ---
  const inputClass = 'w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500';
  const readOnlyClass = 'text-sm text-gray-700';
  const sectionHeadingClass = 'text-sm font-semibold text-gray-700 mb-3';

  const ReadOnlyField: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    value ? (
      <div className="flex justify-between py-1.5">
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-900">{value}</span>
      </div>
    ) : null
  );

  const ReadOnlyAddress: React.FC<{ label: string; addr?: Address }> = ({ label, addr }) => (
    addr?.line1 ? (
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <h4 className={sectionHeadingClass}>{label}</h4>
        <p className={readOnlyClass}>
          {addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}<br />
          {addr.city}, {addr.state} {addr.postalCode}<br />
          {addr.country}
        </p>
      </div>
    ) : null
  );

  const ReadOnlyContact: React.FC<{ label: string; contact?: Contact }> = ({ label, contact }) => (
    contact?.name ? (
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <h4 className={sectionHeadingClass}>{label}</h4>
        <p className={readOnlyClass}>
          {contact.name}{contact.title ? ` — ${contact.title}` : ''}<br />
          {contact.email} | {contact.phone}
        </p>
      </div>
    ) : null
  );

  const SectionBadge: React.FC = () => (
    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
      Update Required
    </span>
  );

  const renderAddressEditor = (
    label: string,
    addr: Address,
    setAddr: React.Dispatch<React.SetStateAction<Address>>
  ) => (
    <div>
      <h4 className={sectionHeadingClass}>{label} <SectionBadge /></h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Address Line 1 *</label>
          <input type="text" value={addr.line1} onChange={e => setAddr({ ...addr, line1: e.target.value })} className={inputClass} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Address Line 2</label>
          <input type="text" value={addr.line2} onChange={e => setAddr({ ...addr, line2: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">City *</label>
          <input type="text" value={addr.city} onChange={e => setAddr({ ...addr, city: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">State/Province *</label>
          <input type="text" value={addr.state} onChange={e => setAddr({ ...addr, state: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Postal Code *</label>
          <input type="text" value={addr.postalCode} onChange={e => setAddr({ ...addr, postalCode: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Country *</label>
          <input type="text" value={addr.country} onChange={e => setAddr({ ...addr, country: e.target.value })} className={inputClass} />
        </div>
      </div>
    </div>
  );

  const renderContactEditor = (
    label: string,
    contact: Contact,
    setContact: React.Dispatch<React.SetStateAction<Contact>>,
    required: boolean
  ) => (
    <div>
      <h4 className={sectionHeadingClass}>{label} <SectionBadge /> {!required && <span className="text-gray-400 font-normal">(Optional)</span>}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name {required ? '*' : ''}</label>
          <input type="text" value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Title</label>
          <input type="text" value={contact.title} onChange={e => setContact({ ...contact, title: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Email {required ? '*' : ''}</label>
          <input type="email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Phone {required ? '*' : ''}</label>
          <input type="text" value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} className={inputClass} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-accent-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-primary shadow-glow-lg mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h1 className="text-4xl font-display font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
              Check Registration Status
            </h1>
            <p className="text-gray-600 text-lg">Enter your registration number and email to check your status</p>
          </div>

          {/* Success Message */}
          {submitSuccess && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-6 text-center animate-fadeIn">
              <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-green-800 mb-1">Registration Resubmitted!</h3>
              <p className="text-green-700">Your updated registration has been sent for review. You will be notified once it's reviewed.</p>
            </div>
          )}

          {/* Lookup Form */}
          {!isEditing && !submitSuccess && (
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-card-hover p-8 border border-gray-100 animate-fadeIn">
              <form onSubmit={handleLookup} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Registration Number</label>
                  <input
                    type="text"
                    value={regNumber}
                    onChange={(e) => setRegNumber(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., CUST-2026-0001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder="Email used during registration"
                  />
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3.5 bg-gradient-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-glow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? 'Looking up...' : 'Check Status'}
                </button>
              </form>

              {/* Status Result (non-editable) */}
              {result && result.status !== 'changes_requested' && statusInfo && (
                <div className={`mt-6 p-6 rounded-2xl border ${statusInfo.bg}`}>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Registration: <span className="font-mono font-semibold">{result.registrationNumber}</span></p>
                    <p className="text-sm text-gray-500 mb-3">{result.entityName}</p>
                    <div className={`text-xl font-bold ${statusInfo.color}`}>
                      {statusInfo.label}
                    </div>
                    {result.status === 'pending' && (
                      <p className="text-sm text-gray-600 mt-2">Your registration is being reviewed. You'll receive an email once a decision is made.</p>
                    )}
                    {result.status === 'approved' && (
                      <p className="text-sm text-gray-600 mt-2">Your registration has been approved. Welcome!</p>
                    )}
                    {result.status === 'rejected' && (
                      <p className="text-sm text-gray-600 mt-2">Your registration was not approved. Please contact support for details.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Edit Form (changes_requested) */}
          {isEditing && result && (
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-card-hover p-8 border border-gray-100 animate-fadeIn">
              {/* Review Notes Banner */}
              {result.approvalNotes && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-semibold text-blue-800 mb-1">Changes Requested by Reviewer</h3>
                      <p className="text-sm text-blue-700">{result.approvalNotes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sections to update list */}
              {sections.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h3 className="text-sm font-semibold text-amber-800 mb-2">Sections that require updates:</h3>
                  <div className="flex flex-wrap gap-2">
                    {sections.map(s => (
                      <span key={s} className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                        {SECTION_LABELS[s] || s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Read-only header fields */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <h3 className={sectionHeadingClass}>Registration Details (Read Only)</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Number:</span> <span className="font-mono font-semibold">{result.registrationNumber}</span></div>
                  <div><span className="text-gray-500">Type:</span> <span className="capitalize font-medium">{result.registrationType}</span></div>
                  <div><span className="text-gray-500">Entity:</span> <span className="font-medium">{result.entityName}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium">{result.email}</span></div>
                  <div><span className="text-gray-500">Tax ID:</span> <span className="font-medium">{result.taxId}</span></div>
                </div>
              </div>

              <form onSubmit={handleResubmit} className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Edit & Resubmit</h3>

                {/* Contact Info */}
                {hasSection('contact_info') ? (
                  <div>
                    <h4 className={sectionHeadingClass}>Phone / Contact Info <SectionBadge /></h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Phone *</label>
                        <input type="text" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Mobile</label>
                        <input type="text" value={editMobile} onChange={(e) => setEditMobile(e.target.value)} className={inputClass} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className={sectionHeadingClass}>Phone / Contact Info</h4>
                    <ReadOnlyField label="Phone" value={result.phone} />
                    <ReadOnlyField label="Mobile" value={result.mobile} />
                  </div>
                )}

                {/* Primary Contact */}
                {hasSection('primary_contact') ? (
                  renderContactEditor('Primary Contact', editPrimaryContact, setEditPrimaryContact, true)
                ) : (
                  <ReadOnlyContact label="Primary Contact" contact={result.primaryContact} />
                )}

                {/* Secondary Contact */}
                {hasSection('secondary_contact') ? (
                  renderContactEditor('Secondary Contact', editSecondaryContact, setEditSecondaryContact, false)
                ) : result.secondaryContact?.name ? (
                  <ReadOnlyContact label="Secondary Contact" contact={result.secondaryContact} />
                ) : null}

                {/* Business Address */}
                {hasSection('business_address') ? (
                  renderAddressEditor('Business Address', editBusinessAddress, setEditBusinessAddress)
                ) : (
                  <ReadOnlyAddress label="Business Address" addr={result.businessAddress} />
                )}

                {/* Remittance Address */}
                {hasSection('remittance_address') ? (
                  renderAddressEditor('Remittance Address', editRemittanceAddress, setEditRemittanceAddress)
                ) : (
                  <ReadOnlyAddress label="Remittance Address" addr={result.remittanceAddress} />
                )}

                {/* Financial Info */}
                {hasSection('financial_info') ? (
                  <div>
                    <h4 className={sectionHeadingClass}>Financial Information <SectionBadge /></h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Payment Terms *</label>
                        <input type="text" value={editPaymentTerms} onChange={(e) => setEditPaymentTerms(e.target.value)} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Currency</label>
                        <input type="text" value={editCurrency} onChange={(e) => setEditCurrency(e.target.value)} className={inputClass} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className={sectionHeadingClass}>Financial Information</h4>
                    <ReadOnlyField label="Payment Terms" value={result.paymentTerms} />
                    <ReadOnlyField label="Currency" value={result.currency} />
                  </div>
                )}

                {/* Bank Details */}
                {hasSection('bank_details') ? (
                  <div>
                    <h4 className={sectionHeadingClass}>Bank Details <SectionBadge /></h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Bank Name *</label>
                        <input type="text" value={editBankInfo.bankName} onChange={e => setEditBankInfo({ ...editBankInfo, bankName: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Account Holder *</label>
                        <input type="text" value={editBankInfo.accountHolderName} onChange={e => setEditBankInfo({ ...editBankInfo, accountHolderName: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Account Number *</label>
                        <input type="text" value={editBankInfo.accountNumber} onChange={e => setEditBankInfo({ ...editBankInfo, accountNumber: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Routing Number</label>
                        <input type="text" value={editBankInfo.routingNumber} onChange={e => setEditBankInfo({ ...editBankInfo, routingNumber: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">SWIFT/BIC Code</label>
                        <input type="text" value={editBankInfo.swiftCode} onChange={e => setEditBankInfo({ ...editBankInfo, swiftCode: e.target.value })} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">IBAN</label>
                        <input type="text" value={editBankInfo.iban} onChange={e => setEditBankInfo({ ...editBankInfo, iban: e.target.value })} className={inputClass} />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">Bank Address</label>
                        <input type="text" value={editBankInfo.bankAddress} onChange={e => setEditBankInfo({ ...editBankInfo, bankAddress: e.target.value })} className={inputClass} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className={sectionHeadingClass}>Bank Details</h4>
                    <ReadOnlyField label="Bank Name" value={result.bankInfo?.bankName} />
                    <ReadOnlyField label="Account Holder" value={result.bankInfo?.accountHolderName} />
                    <ReadOnlyField label="Account Number" value={result.bankInfo?.accountNumber ? `****${result.bankInfo.accountNumber.slice(-4)}` : undefined} />
                  </div>
                )}

                {/* Business Details */}
                {hasSection('business_details') ? (
                  <div>
                    <h4 className={sectionHeadingClass}>Business Details <SectionBadge /></h4>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Business Type *</label>
                      <input type="text" value={editBusinessType} onChange={(e) => setEditBusinessType(e.target.value)} className={inputClass} />
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className={sectionHeadingClass}>Business Details</h4>
                    <ReadOnlyField label="Business Type" value={result.businessType} />
                  </div>
                )}

                {/* Shipper Details (supplier only) */}
                {result.registrationType === 'supplier' && (
                  hasSection('shipper_details') ? (
                    <div>
                      <h4 className={sectionHeadingClass}>Shipper Details <SectionBadge /></h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Shipper Name</label>
                          <input type="text" value={editShipperDetails.shipperName} onChange={e => setEditShipperDetails({ ...editShipperDetails, shipperName: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Account Number</label>
                          <input type="text" value={editShipperDetails.shipperAccountNumber} onChange={e => setEditShipperDetails({ ...editShipperDetails, shipperAccountNumber: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Contact</label>
                          <input type="text" value={editShipperDetails.shipperContact} onChange={e => setEditShipperDetails({ ...editShipperDetails, shipperContact: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Freight Terms</label>
                          <input type="text" value={editShipperDetails.freightTerms} onChange={e => setEditShipperDetails({ ...editShipperDetails, freightTerms: e.target.value })} className={inputClass} />
                        </div>
                      </div>
                    </div>
                  ) : result.shipperDetails?.shipperName ? (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h4 className={sectionHeadingClass}>Shipper Details</h4>
                      <ReadOnlyField label="Shipper Name" value={result.shipperDetails.shipperName} />
                      <ReadOnlyField label="Freight Terms" value={result.shipperDetails.freightTerms} />
                    </div>
                  ) : null
                )}

                {/* Notes */}
                {hasSection('notes') ? (
                  <div>
                    <h4 className={sectionHeadingClass}>Notes <SectionBadge /></h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Notes</label>
                        <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Special Requirements</label>
                        <textarea value={editSpecialReqs} onChange={(e) => setEditSpecialReqs(e.target.value)} rows={3} className={inputClass} />
                      </div>
                    </div>
                  </div>
                ) : (result.notes || result.specialRequirements) ? (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className={sectionHeadingClass}>Notes</h4>
                    <ReadOnlyField label="Notes" value={result.notes} />
                    <ReadOnlyField label="Special Requirements" value={result.specialRequirements} />
                  </div>
                ) : null}

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setIsEditing(false); setResult(null); }}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-gradient-primary text-white rounded-xl font-semibold shadow-lg hover:shadow-glow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? 'Resubmitting...' : 'Resubmit Registration'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors group"
            >
              <svg className="w-4 h-4 mr-1.5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default RegistrationLookupPage;
